import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Input, message, Button, Tooltip, Alert, Badge } from 'antd';
import {  SyncOutlined, ReloadOutlined, DatabaseOutlined } from '@ant-design/icons';
import './index.scss';

const { Search } = Input;

const Email = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [error, setError] = useState(null);
  const [dbInfo, setDbInfo] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const shouldUpdateTotal = useRef(true);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      fixed: 'left',
    },
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
      width: 100,
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
      width: 100,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 250,
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 200,
    },
    {
      title: 'Company Name',
      dataIndex: 'CompanyName',
      key: 'CompanyName',
      width: 200,
    },
    {
      title: 'Tax ID/ABN/VAT',
      dataIndex: 'TaxIDAbnVatNumber',
      key: 'TaxIDAbnVatNumber',
      width: 200,
    },
    {
      title: 'Address 1',
      dataIndex: 'Address1',
      key: 'Address1',
      width: 150,
    },
    {
      title: 'Postcode/ZIP',
      dataIndex: 'PostcodeZIP',
      key: 'PostcodeZIP',
      width: 100,
    },
    {
      title: 'Address 2',
      dataIndex: 'Address2',
      key: 'Address2',
      width: 150,
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 100,
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
      width: 100,
    },
    {
      title: 'Business Type',
      dataIndex: 'BusinessType',
      key: 'BusinessType',
      width: 120,
    },
    {
      title: 'Update Time',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (time) => time ? new Date(time).toLocaleString() : '-',
    },
    {
      title: 'Website Link',
      dataIndex: 'WebsiteLink',
      key: 'WebsiteLink',
      width: 150,
      render: (link) => {
        if (!link) return '-';
        
        // 确保链接有正确的协议前缀
        let fullUrl = link;
        if (!link.startsWith('http://') && !link.startsWith('https://')) {
          fullUrl = `https://${link}`;
        }
        
        return (
          <a href={fullUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
            {link.length > 30 ? `${link.substring(0, 30)}...` : link}
          </a>
        );
      },
    },
    {
      title: 'Message',
      dataIndex: 'Message',
      key: 'Message',
      width: 600,
      render: (message) => message ? (
        <div style={{ maxWidth: '600px' }}>
          {message}
        </div>
      ) : '-',
    },
  ];

  const fetchData = useCallback(async () => {
    if (loading) return; // Prevent concurrent requests
    
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: pagination.current,
        pageSize: pagination.pageSize,
      });
      
      if (searchText) {
        params.append('search', searchText);
      }

      // 直接查询email-kit数据
      const response = await fetch(`/api/email-kit/emails?${params}`);
      const result = await response.json();
      
      if (result.code === 200) {
        setData(result.data.records || []);
        
        // Only update the total, not the current page or pageSize
        if (shouldUpdateTotal.current) {
          setPagination(prev => ({
            ...prev,
            total: result.data.total || 0,
          }));
        }
        
        if (result.data.records && result.data.records.length === 0 && searchText) {
          message.info(`No records found matching "${searchText}"`);
        }
      } else {
        setError(result.message || 'Failed to fetch data');
        message.error(result.message || 'Failed to fetch data');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(`Query failed: ${error.message}`);
      message.error(`Query failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [searchText, pagination.current, pagination.pageSize, loading]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 直接查询所有email-kit数据
      const response = await fetch('/api/email-kit/all-emails');
      const result = await response.json();
      if (result.code === 200) {
        setData(result.data.records || []);
        shouldUpdateTotal.current = true;
        setPagination(prev => ({
          ...prev,
          total: result.data.total || 0,
          current: 1,
          pageSize: result.data.total || 10 
        }));
        
        message.success(`Successfully retrieved all ${result.data.total} records`);
      } else {
        setError(result.message || 'Failed to fetch all data');
        message.error(result.message || 'Failed to fetch all data');
      }
    } catch (error) {
      console.error('Error fetching all data:', error);
      setError(`Failed to query all data: ${error.message}`);
      message.error(`Failed to query all data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    shouldUpdateTotal.current = true;
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (newPagination) => {
    shouldUpdateTotal.current = false; // Don't update total when just changing page
    setPagination(newPagination);
  };

  // 获取数据库统计信息
  const fetchDatabaseStats = useCallback(async () => {
    try {
      const response = await fetch('/api/email-kit/statistics');
      const result = await response.json();
      
      if (result.code === 200) {
        setDbInfo(result.data);
      }
    } catch (error) {
      console.error('Error fetching database stats:', error);
    }
  }, []);

  // 测试连接email-kit数据库
  const testConnection = useCallback(async () => {
    try {
      setConnectionStatus('testing');
      const response = await fetch('/api/email-kit/test');
      const result = await response.json();
      
      if (result.code === 200) {
        setConnectionStatus('success');
        message.success(result.message);
        
        // 获取数据库统计信息
        fetchDatabaseStats();
      } else {
        setConnectionStatus('failed');
        message.error(result.message || 'Connection test failed');
      }
    } catch (error) {
      setConnectionStatus('failed');
      message.error(`Connection test failed: ${error.message}`);
    }
  }, [fetchDatabaseStats]);
  
  // 重置搜索
  const resetSearch = () => {
    setSearchText('');
    shouldUpdateTotal.current = true;
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Use a more stable dependency for the fetch effect
  const paginationDep = JSON.stringify({
    current: pagination.current,
    pageSize: pagination.pageSize
  });
  
  useEffect(() => {
    fetchData();
  }, [paginationDep, searchText]); // Remove fetchData from dependencies to prevent double calls

  // 组件加载时测试连接
  useEffect(() => {
    testConnection();
  }, [testConnection]);

  return (
    <div className="email-page">
      <div className="email-header">
        <div className="email-header-title">
          <h2>Email-Kit Data Query</h2>
          {connectionStatus && (
            <Badge 
              status={connectionStatus === 'success' ? 'success' : connectionStatus === 'failed' ? 'error' : 'processing'} 
              text={connectionStatus === 'success' ? 'Connected' : connectionStatus === 'failed' ? 'Connection Failed' : 'Connecting...'}
              style={{ marginLeft: 8 }}
            />
          )}
          {dbInfo && (
            <span className="db-info">
              (Total Records: {dbInfo.total}, Database: {dbInfo.database})
            </span>
          )}
        </div>
        <div className="email-header-tools">
          <Search
            placeholder="Search by name, email, company, country, etc."
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={handleSearch}
            style={{ width: 300 }}
            enterButton
          />
          
          <Button 
            icon={<ReloadOutlined />} 
            onClick={resetSearch}
            style={{ marginLeft: 8 }}
            title="Reset Search"
          />
          
          <Button
            icon={<DatabaseOutlined />}
            onClick={fetchAllData}
            style={{ marginLeft: 8 }}
            title="Get All Data"
          >
            Get All
          </Button>
          
          <Tooltip title="Test Email-Kit Database Connection">
            <Button 
              icon={<SyncOutlined spin={connectionStatus === 'testing'} />} 
              onClick={testConnection}
              style={{ marginLeft: 8 }}
              type={connectionStatus === 'success' ? 'primary' : connectionStatus === 'failed' ? 'danger' : 'default'}
            />
          </Tooltip>
        </div>
      </div>

      {error && (
        <Alert
          message="Query Error"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
          onClose={() => setError(null)}
        />
      )}
      
      {connectionStatus === 'failed' && (
        <Alert
          message="Database Connection Failed"
          description="Unable to connect to Email-Kit database. Please check your network connection or database configuration"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        rowKey="id"
        scroll={{ x: 1800 }}
      />
    </div>
  );
};

export default Email; 